define('models/AnalyticsData',[
    'lib/knockout',
    'lib/lodash4',
    'moment',
    'services/intervalService',
    'services/loggingService'
], function (
    ko,
    _,
    moment,
    intervalService,
    log
) {
    'use strict';

    var AnalyticsData;

    // **TODO** (5/25/2014): This logic is added for Interactions 2014 and should
    // be refactored to utilize the right backend API.
    var CURRENT_OBSERVATIONS_METRICS = [
        "oWaiting",
        "oInteracting",
        "oActiveUsers",
        "oMemberUsers",
        "oOnQueueUsers",
        "oOffQueueUsers"
    ];

    var CHANGED_METRICS_MAP = {
        "serviceLevel": "oServiceLevel",
        "serviceTarget": "oServiceTarget",
        "nActive": "oActiveUsers",
        "nMembers": "oMemberUsers",
        "tHeldCompleted": "tHeldComplete",
        "tTalkCompleted": "tTalkComplete"
    };

    // ===================================================================================
    // Private Methods
    // ===================================================================================

    /**
     * Determine if a source object has a value. This method will determine if the data
     * object is defined regardless if the object argument is a plain object or an
     * instance of AnalyticsData.
     *
     * @param  {Object|AnalyticsData}  obj The object to check if it is defined. If the
     *  argument is an instance of the AnalyticsData object, then this method will check
     *  if the rawData() attribute has a value.
     * @return {Boolean} true if the value is defined, false otherwise.
     */
    var isSourceObjectDefined = function (obj) {
        var source = ko.unwrap(obj);

        if (_.isUndefined(source) || _.isNull(source)) {
            // if the source data is a normal object and does not have a value, return
            // false
            return false;
        }

        if (source instanceof AnalyticsData && !source.rawData()) {
            // if the source is an AnalyticsDataObject but does not have its rawData
            // attribute defined, return false
            return false;
        }

        return true;
    };

    /**
     * This method will convert a stat notification payload into an object that better
     * conforms to the expected format of values in the rawData attribute.
     * It will return the transformed object.
     *
     * For example:
     *
     * # Before
     *
     * {
     *     "interval":"2014-04-29T11:00:00.000-04:00/2014-04-29T11:30:00.000-04:00",
     *     "entity":{
     *         "kind":"user",
     *         "id":"9982aa70-bff1-4c0b-8b4c-39d0318c89f2"
     *     },
     *     "metrics":{
     *         "tis.6a3af858-942f-489d-9700-5f9bcdcdae9b":{
     *             "count":1.0,
     *             "min":491986.0,
     *             "sum":491986.0,
     *             "max":491986.0
     *         }
     *      }
     * }
     *
     * # After
     *
     * {
     *     entity: {
     *         "kind":"user",
     *         "id":"9982aa70-bff1-4c0b-8b4c-39d0318c89f2"
     *     },
     *     data: [
     *         {
     *             "interval":"2014-04-29T11:00:00.000-04:00/2014-04-29T11:30:00.000-04:00",
     *             "metrics":{
     *             "tis.6a3af858-942f-489d-9700-5f9bcdcdae9b":{
     *                 "count":1.0,
     *                 "min":491986.0,
     *                 "sum":491986.0,
     *                 "max":491986.0
     *             }
     *         }
     *     ]
     * }
     *
     * @param  {Object} notificationObject The raw object representing the stat
     *  notification
     * @return {Object} The transformed object.
     */
    var convertNotificationObjectToRawDataObject = function (notificationObject) {
        notificationObject.metrics = renameMetrics(notificationObject.metrics);

        var result = _.pick(notificationObject, "entity");
        var intervalObj = _.pick(notificationObject, "interval", "metrics");
        intervalObj.metrics = _.filter(intervalObj.metrics, function (metric) {
            if (!_.isUndefined(metric.metric)) {
                return !isObservationMetric(metric.metric);
            }
        });

        result.data = [
            intervalObj
        ];
        result.currentObservations = _.filter(notificationObject.metrics, function (metric) {
            return isObservationMetric(metric.metric);
        });

        // handle new member objects
        if (_.isEmpty(result.currentObservations) && !_.isUndefined(notificationObject.currentObservations)) {
            result.currentObservations = _.filter(notificationObject.currentObservations, function (metric) {
                return isObservationMetric(metric.metric);
            });
        }

        return result;
    };

    var reformatDataMetrics = function (data) {
        if (_.isPlainObject(data.metrics)) {
            data.metrics = _.map(data.metrics, function (value, key) {
                var metric = {
                    metric: key
                };
                if (!_.isUndefined(value)) {
                    metric.stats = value;
                }
                return metric;
            });
        }

        if (_.isPlainObject(data.currentObservations)) {
            data.currentObservations = _.map(data.currentObservations, function (value, key) {
                var metric = {
                    metric: key
                };
                if (_.isNumber(value)) {
                    metric.stats = {
                        count: value
                    };
                } else {
                    metric.stats = value;
                }
                return metric;
            });
        }

        return data;
    };

    var reformatObservations = function (resultData) {
        var observations = _.filter(resultData.data, function (metric) {
            return isObservationMetric(metric.metric);
        });

        resultData.data = _.filter(resultData.data, function (metric) {
            return !isObservationMetric(metric.metric);
        });
        if (!resultData.currentObservations) {
            resultData.currentObservations = [];
        }
        resultData.currentObservations = resultData.currentObservations.concat(observations);

        return resultData;
    };

    var renameMetrics = function (dataMetrics) {
        return _.map(dataMetrics, function (metric) {
            var key = metric.metric;
            var metricQualifier = key.split(".");
            if (metricQualifier.length > 1) {
                metric.metric = metricQualifier[0];
                metric.qualifier = metricQualifier[1];
            }
            if (CHANGED_METRICS_MAP[key]) {
                metric.metric = CHANGED_METRICS_MAP[key];
            }
            return metric;
        });
    };

    var isObservationMetric = function (metric) {
        var observation = _.find(CURRENT_OBSERVATIONS_METRICS, function (observation) {
            // needed to handle things like 'onQueueUsers.IDLE'
            return _.includes(observation, metric);
        });
        return !_.isUndefined(observation);
    };

    var generateRawData = function (data) {
        var rawData = data;

        if (!_.isEmpty(rawData) && !_.has(rawData, "data") && _.has(rawData, "entity")) {
            rawData = reformatDataMetrics(rawData);
            rawData = convertNotificationObjectToRawDataObject(rawData);
        } else if (rawData) {
            rawData = reformatObservations(rawData);
        }

        return rawData;
    };

    /**
     * A wrapper method to help set the value for the rawData attribute of an
     * AnalyticsData object. This method helps to seemlessly set the rawData attribute in
     * regardless of whether the dataObj argument represents a stat notification or an
     * analytics query response.
     *
     * @param {AnalyticsData} destinationObj The AnalyticsData object whose rawData()
     *  attribute will be set
     * @param {Object} newValue The value to set the rawData attribute to.
     */
    var setRawData = function (destinationObj, newValue) {
        var newRawData = generateRawData(newValue);

        ko.unwrap(destinationObj).rawData(newRawData);
    };

    /**
     * Combine the raw analytics data from a source object into an instance of
     * AnalyticsData. This method will seemlessly combine source objects regardless of
     * whether the data is from another AnalyticsData object, a query response payload, or
     * a stat notification.
     *
     * @param  {AnalyticsData} destinationData-  An instance of AnalyticsData who will have
     *  its rawData attribute updated.
     * @param  {Object|AnalyticeData} sourceData - The analytics data to combine into the
     *  destination argument.
     * @param  {Function} strategyFunction - A function that will be executed to determine
     *  how the combination of two existing data sets should occur. This function will
     *  take the existing interval object in the destination and the interval object from
     *  the source to determine how the source should be combined with the existing
     *  interval metrics data in the destination. This is primarily used to determine if a
     *  fill or a merge should occur.
     */
    var combineRawAnalyticsData = function (destinationData, sourceData, strategyFunction) {
        var destination = ko.unwrap(destinationData);

        if (!destination) {
            destination = new AnalyticsData();
        }

        var destinationRawData = ko.unwrap(destination).rawData();
        var source;

        if (!isSourceObjectDefined(sourceData)) {
            log.debug("The source data was undefined or null when trying to combine analytics data", destinationData, sourceData, strategyFunction);
            return null; // don't do anything if source is undefined
        }

        if (_.isPlainObject(sourceData)) {
            if (_.has(sourceData, "results")) {
                source = sourceData.results[0];
            } else {
                source = sourceData;
            }
        } else {
            source = ko.unwrap(sourceData).rawData();
        }

        if (source && source.metrics) {
            source.metrics = renameMetrics(source.metrics);
            source.currentObservations = renameMetrics(source.currentObservations);
        }

        if (!destinationRawData || !destinationRawData.data) {
            // If the destination raw data is not defined, just set its value
            setRawData(destination, source);
        } else {
            var combineSourceObjectIntoDestinationObject = function (sourceIntervalObj) {
                var intervalDestination = _.find(destinationRawData.data, {interval: sourceIntervalObj.interval});
                var sourceInterval = sourceIntervalObj;

                if (sourceIntervalObj.entity) {
                    // convert the stat notification to an interval object that conforms
                    // to the expected format for the rawData attribute.
                    sourceInterval = _.pick(sourceIntervalObj, "interval", "metrics");

                    // **TODO** (5/25/2014): This logic is added for Interactions 2014 and should
                    // be refactored to utilize the right backend API.
                    sourceInterval.metrics = _.filter(sourceInterval.metrics, function (metric) {
                        return !isObservationMetric(metric.metric);
                    });
                }

                if (_.isUndefined(intervalDestination)) {
                    // If an interval is not found then add the new interval to the
                    // rawDataToFill object's collection of metrics.
                    destinationRawData.data.push(sourceInterval);
                } else {
                    strategyFunction(intervalDestination.metrics, sourceInterval.metrics);
                }
            };

            if (source.data) {
                _.each(source.data, combineSourceObjectIntoDestinationObject);
            } else {
                combineSourceObjectIntoDestinationObject(source);
            }

            var sourceCurrentObservations = source.currentObservations;
            if (!_.isEmpty(source.currentObservations)) {
                sourceCurrentObservations = _.filter(source.currentObservations, function (metric) {
                    return isObservationMetric(metric.metric);
                });
            } else if (source.metrics) {
                sourceCurrentObservations = _.filter(source.metrics, function (metric) {
                    return isObservationMetric(metric.metric);
                });
            }

            if (!destinationRawData.currentObservations) {
                destinationRawData.currentObservations = [];
            }

            strategyFunction(destinationRawData.currentObservations, sourceCurrentObservations);

            // Tell subscribers that the observable value has changed.
            ko.unwrap(destination).rawData.valueHasMutated();
        }

        if (ko.isObservable(destinationData)) {
            // initialize destinationData into an analytics data object if it does not
            // have a value yet and is an observable.
            destinationData(destination);
        }

        return destination;
    };

    // ===================================================================================
    // Class Definition
    // ===================================================================================

    /*
     * A private utility class that helps expose reusable computations on a single result object
     * from the analytics service. The result object contains all of the data objects inside of an array named 'data'.
     * The data array can contain multiple objects, each consisting of an interval and metrics.
     * This object provides helper methods to compute sums and averages across multiple response objects.
     * This *may* become obsolete as the analytics api now returns averages and sums.
     *
     * See Call Flow and Metrics from the Analytics API V2:
     *
     * https://confluence.inin.com/pages/viewpage.action?pageId=61452876
     */
    AnalyticsData = function (rawData) {
        var self = this;

        self.rawData = ko.observable(generateRawData(rawData));

        self.entityId = ko.computed(function () {
            if (_.isEmpty(self.rawData())) {
                return undefined;
            }

            if (self.rawData().entity) {
                return self.rawData().entity.id;
            }

            // get the dynamic key that contains the entity's id
            var queryEntityData = self.rawData().group;
            if (queryEntityData) {
                var idKey = _.find(Object.keys(queryEntityData), function (key) {
                    return key.slice(-2).toLowerCase() === "id";
                });
                if (idKey) {
                    return queryEntityData[idKey];
                }

                // Pull out the value from the entity's key name (which is dynamic) and return
                // it as the id.
                return queryEntityData.userId || queryEntityData.queueId || queryEntityData.outboundCampaignId;
            }

            return undefined;
        });

        self.getCurrentObservationMetric = function (key) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().currentObservations)) {
                return null;
            }

            var observationMetric = _.find(self.rawData().currentObservations, {metric: key});
            if (!_.isUndefined(observationMetric) && !_.isUndefined(observationMetric.stats)) {
                return observationMetric.stats.count;
            }
            return null;
        };

        /*
         * Slice the data currently stored in rawData() into a subset determined by the
         * interval provided as an argument to this getDataForInterval().
         *
         * @param {String|Moment} A string representing an ISO8601 interval or a moment
         *                        object that indicates the start of the interval
         * @param {Moment} If the first argument is a moment object, this argument is
         *                 required to included. It should be a moment object that
         *                 indicates the end of the interval.
         * @returns A new instance of AnalyticsData that contains the subset of data found
         *          for the requested interval. If not data is found or there is a problem
         *          with the arguments, null will be returned.
         */
        self.getDataForInterval = function (startInterval, endInterval) {
            if (!self.rawData() || !self.rawData().data) {
                log.debug("rawData and/or rawData().data does not have a value", self.rawData());
                return null;
            }

            if (!startInterval || (typeof startInterval != "string" && !endInterval)) {
                log.error("getDataForInterval() - The interval arguments to the method must be an ISO8601 interval string or moment objects.");
                return null;
            }

            var startMoment = startInterval;
            var endMoment = endInterval;

            if (_.isString(startInterval)) {
                var intervalStrs = intervalService.splitIntervalString(startInterval);

                if (_.isNull(intervalStrs)) {
                    return null;
                }

                startMoment = moment(intervalStrs[0]);
                endMoment = moment(intervalStrs[1]);
            }

            if (!startMoment.isValid() || !endMoment.isValid()) {
                log.error("getDataForInterval() - Both arguments to getDataForInterval must be moment objects");
                return null;
            }

            var filteredData = _.filter(self.rawData().data, function (obj) {
                var times = intervalService.splitIntervalString(obj.interval);

                var startTime = times[0];
                var endTime = times[1];

                var isAfterStart = startMoment.isBefore(startTime) || startMoment.isSame(startTime);
                var isBeforeEnd = endMoment.isAfter(endTime) || endMoment.isSame(endTime);

                if (isAfterStart && isBeforeEnd) {
                    return true;
                }

                return false;
            });

            if (filteredData.length === 0) {
                return null;
            }

            var newRawData = _.clone(self.rawData(), true);

            newRawData.data = filteredData;

            return new AnalyticsData(newRawData);
        };

        /**
         * Get the object for a given key in the "metrics" object for an interval. The
         * object returned is determined by the provided start and end interval arguments.
         *
         * This mehtod will look for an exact match in the intervals provided to it and
         * the intervals in the collection of data that the instance is managing. Fuzzy
         * matching of intervals is not supported because determining the exact metric
         * object to return does not work if mupltiple interval objects are found.
         *
         * @param  {String} metricKey     The key name of the metric to look for.
         * @param  {[type]} startInterval A string representing an ISO8601 interval or a
         *  moment object that indicates the start of the interval
         * @param  {[type]} endInterval   If the first argument is a moment object, this
         *  argument is required. It should be a moment object that represents the end
         *  time of the interval.
         *
         * @return {Object|Null} An object containing the statistics that represent the
         *  requested metric key. If an object is not found, null is returned.
         */
        self.getMetricObjectForInterval = function (metricKey, startInterval, endInterval) {
            if (!self.rawData() || !self.rawData().data) {
                log.debug("rawData and/or rawData().data does not have a value", self.rawData());
                return null;
            }

            if (!startInterval || (!_.isString(startInterval) && !endInterval)) {
                log.error("getDataForInterval() - The interval arguments to the method must be an ISO8601 interval string or moment objects.");
                return null;
            }

            var startMoment = startInterval;
            var endMoment = endInterval;

            if (_.isString(startInterval)) {
                var intervalStrs = intervalService.splitIntervalString(startInterval);

                if (_.isNull(intervalStrs)) {
                    return null;
                }

                startMoment = moment(intervalStrs[0]);
                endMoment = moment(intervalStrs[1]);
            }

            if (!startMoment.isValid() || !endMoment.isValid()) {
                log.error("getDataForInterval() - Both arguments to getDataForInterval must be moment objects");
                return null;
            }

            var metricObj = _.find(self.rawData().data, function (dataObj) {
                var times = intervalService.splitIntervalString(dataObj.interval);

                var startTime = times[0];
                var endTime = times[1];

                return (startMoment.isSame(startTime) && endMoment.isSame(endTime));
            });

            if (_.isUndefined(metricObj) || !_.find(metricObj.metrics, {metric: metricKey})) {
                return null;
            }

            return _.find(metricObj.metrics, {metric: metricKey});
        };

        /*
        * Get a summation of the "count" attribute across all intervals for a given
        * statistic.
        *
        * @param {String} The key name of the statistic to use as a sum.
        * @returns An integer representing that sum of the value for each instance of the
        *          "count" attribute across all intervals. If the key name or count
        *          attribute for the statistic is not found then null will be returned.
        */
        self.sumCount = function (statName) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var hasStat = false;
            var totalCount = 0;

            totalCount = _.reduce(self.rawData().data, function(countSum, intervalObj) {
                if (_.find(intervalObj.metrics, {metric: statName})) {
                    hasStat = true;
                    return countSum + _.find(intervalObj.metrics, {metric: statName}).stats.count;
                }

                return countSum;
            }, 0);

            if (!hasStat) {
                return null;
            }

            return totalCount;
        };

        /*
        * Get a summation of the "sum" attribute across all intervals for a given
        * statistic.
        *
        * @param {String} The key name of the statistic to use.
        * @param {String} The key name of the qualifier to use - OPTIONAL (used for presence data)
        * @returns An integer representing that sum of the value for each instance of the
        *          "sum" attribute across all intervals. If the key name or sum
        *          attribute for the statistic is not found then null will be returned.
        */
        self.sum = function (statName, qualifier) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var hasStat = false;
            var totalValue = 0;

            totalValue = _.reduce(self.rawData().data, function(valueSum, intervalObj) {
                var check = {metric: statName};
                if (qualifier) {
                    check.qualifier = qualifier;
                }
                if (_.find(intervalObj.metrics, check)) {
                    hasStat = true;
                    return valueSum + _.find(intervalObj.metrics, check).stats.sum;
                }

                return valueSum;
            }, 0);

            if (!hasStat) {
                return null;
            }

            return totalValue;
        };

        self.sumMetricAttribute = function (metricName, attribute) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var hasStat = false;
            var totalCount = 0;

            totalCount = _.reduce(self.rawData().data, function(countSum, intervalObj) {
                if (_.find(intervalObj.metrics, {metric: metricName})) {
                    hasStat = true;
                    return countSum + _.find(intervalObj.metrics, {metric: metricName}).stats[attribute];
                }

                return countSum;
            }, 0);

            if (!hasStat || _.isNaN(totalCount)) {
                return null;
            }

            return totalCount;
        };

        self.avgMetricAttribute = function (metricName, attribute) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var hasStat = false;
            var totalCount = 0;
            var dataCount = 0;

            totalCount = _.reduce(self.rawData().data, function(countSum, intervalObj) {
                dataCount++;
                if (_.find(intervalObj.metrics, {metric: metricName})) {
                    hasStat = true;
                    return countSum + _.find(intervalObj.metrics, {metric: metricName}).stats[attribute];
                }

                return countSum;
            }, 0);

            if (!hasStat || _.isNaN(totalCount)) {
                return null;
            }
            if (dataCount > 0) {
                return totalCount / dataCount;
            }
            return totalCount;
        };

        /*
        * Compute the average value of a statistic.
        *
        * @param {String} The key name of the statistic to use as a sum.
        * @returns An integer representing that average value across all intervals.
        *          If the key name for the statistic is not found then null will be
        *          returned.
        */
        self.avg = function (statName) {
            if (_.isUndefined(self.rawData()) || _.isUndefined(self.rawData().data)) {
                return null;
            }

            var average;
            var totalCount = self.sumCount(statName);
            var totalValue = self.sum(statName);

            if (!totalCount || _.isNaN(totalValue)) {
                return null;
            }

            average = totalValue / totalCount;

            return average;
        };
    };

    // ===================================================================================
    // Static Class Methods
    // ===================================================================================

    /**
     * Fills missing pieces of data in an existing AnalyticsData object's rawData
     * attribute with data from a source object. Existing static fields will NOT be
     * overwritten.
     *
     * @param  {AnalyticsData|Observable} destinationData - The instance of an AnalyticsData object
     *  to merge data into.
     *
     *  *Note* - If this value is an observable that is not initialized,
     *  then this method will create an empty instance of AnalyticsData and set the
     *  observable's value to this new instance.
     * @param  {Object|AnalyticsData} sourceData - The source data to merge into
     *  destinationData. This data can be an object that represents the payload returned
     *  from a query to analytics service or an existing instance of an AnalyticsData
     *  object.
     * @return The updated value of the object passed into the "destinationData" argument.
     */
    AnalyticsData.fill = function (destinationData, sourceData) {

        if (sourceData) {
            sourceData = reformatDataMetrics(sourceData);
        }

        return combineRawAnalyticsData(destinationData, sourceData, function (destination, source) {
            // If an interval is found, fill the missing pieces of data while ensuring
            // that existing data is not overwritten.
            _.each(source, function(sourceObj) {
                var destinationObj = _.find(destination, function (destObj) {
                    var metricMatch = destObj.metric === sourceObj.metric;
                    var qualifierMatch = (sourceObj.qualifier) ? destObj.qualifier === sourceObj.qualifier : true;

                    return metricMatch && qualifierMatch;
                });

                if (destinationObj) {
                    _.defaults(destinationObj.stats, sourceObj.stats);
                } else {
                    if (!_.isArray(destination)) {
                        var destMetric = destination;
                        destination = [];
                        destination.push(destMetric);
                        destination.push(_.clone(sourceObj));
                    } else {
                        destination.push(_.clone(sourceObj));
                    }

                }
            });
        });
    };

    /**
     * Merges raw data into an existing AnalyticsData object's rawData attribute. Static
     * fields will be overwritten.
     *
     * @param  {AnalyticsData|Observable} destinationData - The instance of an AnalyticsData object
     *  to merge data into.
     *
     *  *Note* - If this value is an observable that is not initialized,
     *  then this method will create an empty instance of AnalyticsData and set the
     *  observable's value to this new instance.
     * @param  {Object|AnalyticsData} sourceData - The source data to merge into
     *  destinationData. This data can be an object that represents the payload returned
     *  from a query to analytics service or an existing instance of an AnalyticsData
     *  object.
     * @return The updated value of the object passed into the "destinationData" argument.
     */
    AnalyticsData.merge = function (destinationData, sourceData) {

        if (sourceData) {
            sourceData = reformatDataMetrics(sourceData);
        }

        return combineRawAnalyticsData(destinationData, sourceData, function (destination, source) {
            // If an interval is found, merge the pieces of data
            _.each(source, function (obj) {
                var key = obj.metric;
                var destMetric;
                if (obj.qualifier) {
                    destMetric = _.find(destination, {metric: key, qualifier: obj.qualifier});
                } else {
                    destMetric = _.find(destination, {metric: key});
                }
                
                if (destMetric) {
                    _.assign(destMetric.stats, obj.stats);
                } else {
                    if (!_.isArray(destination)) {
                        destMetric = destination;
                        destination = [];
                        destination.push(destMetric);
                    }
                    if (destination.length < 1) {
                        _.assign(destination, source);
                    } else {
                        destination.push(obj);
                        _.assign(destination, destination);
                    }
                }
            });
        });
    };

    return AnalyticsData;
});

